import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import EditableCell from "./EditableCell";
import { Table, Form } from "react-bootstrap";
import { updatePriceInfo } from "state/orderEdition/actions";

const initialData = {
  forfaitPiecesHT: {
    label: "Forfait Pièces HT",
    parPersonne: 0,
    parPersonneEstimation: 0,
    totalClient: 0,
    totalEstimation: 0,
  },
  forfaitSoftsHT: {
    label: "Forfait Softs HT",
    parPersonne: 0,
    parPersonneEstimation: 0,
    totalClient: 0,
    totalEstimation: 0,
  },
  forfaitAlcoolsHT: {
    label: "Forfait Alcools HT",
    parPersonne: 0,
    parPersonneEstimation: 0,
    totalClient: 0,
    totalEstimation: 0,
  },
  forfaitSubTotal: {
    label: "Sous total",
    parPersonne: 0,
    parPersonneEstimation: 0,
    totalClient: 0,
    totalEstimation: 0,
  },
  materielService: {
    label: "Matériel et Service",
    parPersonne: null,
    parPersonneEstimation: null,
    totalClient: 0,
    totalEstimation: 0,
  },
  auDetail: {
    label: "Au détail",
    parPersonne: null,
    parPersonneEstimation: null,
    totalClient: 0,
    totalEstimation: 0,
  },
  livraison: {
    label: "Livraison",
    parPersonne: null,
    parPersonneEstimation: null,
    totalClient: 0,
    totalEstimation: 0,
  },
  totalHT: {
    label: "Total HT",
    parPersonne: null,
    parPersonneEstimation: null,
    totalClient: 0,
    totalEstimation: 0,
  },
};

const getPiecesBreakdown = (items = []) =>
  items.reduce(
    (acc, item) => {
      if (
        item.type !== "productPackage" ||
        item.caterer_price_group === "soft" ||
        item.caterer_price_group === "alcohol"
      ) {
        return acc;
      }

      const aggregate = (collection = []) => {
        collection.forEach((product) => {
          const qty = Number(product.quantity) || 0;
          if (!qty) {
            return;
          }
          if (product.sweet) {
            acc.sweet += qty;
          } else {
            acc.salty += qty;
          }
        });
      };

      aggregate(item.products);
      aggregate(item.product_parts);

      return acc;
    },
    { sweet: 0, salty: 0 }
  );

const formatPiecesLabel = (sweetCount, saltyCount) => {
  const total = sweetCount + saltyCount;
  if (!total) {
    return initialData.forfaitPiecesHT.label;
  }
  return (
    <>
      {initialData.forfaitPiecesHT.label}
      <br />
      <small>
        {`${total} pièces : ${sweetCount} sucrées / ${saltyCount} salées.`}<br/>
      </small>
    </>
  );
};

const PricingTable = ({ orderIndex }) => {
  const dispatch = useDispatch();
  const order = useSelector(
    (state) => state.orderEdition.orders[orderIndex] || {}
  );
  // En mode brique, la tarification est gérée par BriquesSection.
  // Cf. early return plus bas (placé après tous les hooks pour respecter
  // les rules of hooks).
  const briqueMode = order?.briqMode ?? false;
  const items = order.items || [];
  const numberOfPersons = useSelector(
    (state) =>
      state.orderEdition.orders[orderIndex]?.deliveryInfo?.person || 1
  );
  const priceInfo = order.priceInfo || {};
  const useClassicPricing = priceInfo.useClassicPricing;
  const isClassicMode = useMemo(() => useClassicPricing === true, [useClassicPricing]);
  const [data, setData] = useState(initialData);

  // Fonction pour initialiser les données à partir de priceInfo et des items
  const initializeData = (currentOrderPriceInfo, currentIsClassicMode, row, column, value) => {
    const { sweet, salty } = getPiecesBreakdown(items);

    let newData = {
      ...initialData,
      forfaitPiecesHT: {
        ...initialData.forfaitPiecesHT,
        label: formatPiecesLabel(sweet, salty),
        parPersonne: currentIsClassicMode ? null : (currentOrderPriceInfo.catererPackagePiecesPriceHtPerParticipant ?? null),
        parPersonneEstimation: 0,
        totalClient: currentIsClassicMode ? 0 : ((currentOrderPriceInfo.catererPackagePiecesPriceHtPerParticipant ?? 0) * numberOfPersons),
        totalEstimation: 0,
      },
      forfaitSoftsHT: {
        ...initialData.forfaitSoftsHT,
        parPersonne: currentIsClassicMode ? null : (currentOrderPriceInfo.catererPackageSoftsPriceHtPerParticipant ?? null),
        parPersonneEstimation: 0,
        totalClient: currentIsClassicMode ? 0 : ((currentOrderPriceInfo.catererPackageSoftsPriceHtPerParticipant ?? 0) * numberOfPersons),
        totalEstimation: 0,
      },
      forfaitAlcoolsHT: {
        ...initialData.forfaitAlcoolsHT,
        parPersonne: currentIsClassicMode ? null : (currentOrderPriceInfo.catererPackageAlcoholsPriceHtPerParticipant ?? null),
        parPersonneEstimation: 0,
        totalClient: currentIsClassicMode ? 0 : ((currentOrderPriceInfo.catererPackageAlcoholsPriceHtPerParticipant ?? 0) * numberOfPersons),
        totalEstimation: 0,
      },
      materielService: {
        ...initialData.materielService,
        totalClient: 0,
        totalEstimation: 0,
      },
      auDetail: {
        ...initialData.auDetail,
        totalClient: 0,
        totalEstimation: 0,
      },
      livraison: {
        ...initialData.livraison,
        totalClient: 0,
        totalEstimation: 0,
      },
      totalHT: {
        ...initialData.totalHT,
        totalClient: 0,
        totalEstimation: 0,
      },
    };

    if (row) {
      newData = { ...newData, [row]: { ...newData[row], parPersonne: value } };
      if (column === "parPersonne") {
        newData[row].totalClient = value * numberOfPersons;
      }
    }

    // Prix pour les forfaits
    const packageItems = items.filter((item) => item.type === "productPackage");
    packageItems.forEach((packageItem) => {
      const catererPriceGroup = packageItem?.caterer_price_group;
      const priceGroup = catererPriceGroup === "alcohol" ? "forfaitAlcoolsHT" : catererPriceGroup === "soft" ? "forfaitSoftsHT" : "forfaitPiecesHT";
      const totalPrice = packageItem.price * packageItem.quantity;
      
      if (currentIsClassicMode) {
        // En mode classique, on affiche le prix total directement (comme pour les breakfasts)
        newData[priceGroup].totalClient += totalPrice;
        newData[priceGroup].totalEstimation = newData[priceGroup].totalClient;
        // Par personne estimation n'a pas de sens en mode classique, on met null
        newData[priceGroup].parPersonneEstimation = null;
      } else {
        // En mode forfait, on calcule par personne
        const pricePerPerson = totalPrice / numberOfPersons;
        newData[priceGroup].parPersonneEstimation += pricePerPerson;
        newData[priceGroup].totalEstimation = newData[priceGroup].parPersonneEstimation * numberOfPersons;
      }
    });
    
    // Si le prix par personne n'est pas défini dans priceInfo (null) et qu'on n'est pas en mode classique, on le calcule à partir des items
    if (!currentIsClassicMode) {
      ["forfaitPiecesHT", "forfaitSoftsHT", "forfaitAlcoolsHT"].forEach((priceGroup) => {
        if (newData[priceGroup].parPersonne === null && newData[priceGroup].parPersonneEstimation > 0) {
          newData[priceGroup].parPersonne = Math.round(newData[priceGroup].parPersonneEstimation);
          newData[priceGroup].totalClient = Math.round(newData[priceGroup].parPersonne * numberOfPersons);
        }
      });
    }

    // Sous total pour les forfaits
    newData.forfaitSubTotal.parPersonneEstimation = [
      "forfaitPiecesHT",
      "forfaitSoftsHT",
      "forfaitAlcoolsHT",
    ].reduce((total, key) => total + newData[key].parPersonneEstimation, 0);
    newData.forfaitSubTotal.totalEstimation = [
      "forfaitPiecesHT",
      "forfaitSoftsHT",
      "forfaitAlcoolsHT",
    ].reduce((total, key) => total + newData[key].totalEstimation, 0);

    // Prix Matériel et Service
    const materialItems = items.filter(
      (item) => item.classification_name === "Matériels et services"
    );

    materialItems.forEach((materialItem) => {
      const { price, quantity } = materialItem;
      newData.materielService.totalClient += (price * quantity);
      newData.materielService.totalEstimation = newData.materielService.totalClient;
    });

    // Prix Au détail
    const detailItems = items.filter(
      (item) => {
        if (item.classification_name === "Matériels et services") {
          return false;
        }
        // En mode classique, on inclut les productPackage dans "Au détail"
        if (currentIsClassicMode) {
          return item.type !== "productGroup";
        }
        // En mode forfait, on exclut les productPackage
        return item.type !== "productPackage";
      }
    );
    detailItems.forEach((detailItem) => {
      const { price, quantity } = detailItem;
      const perPerson = (price * quantity);
      newData.auDetail.totalClient += perPerson;
      newData.auDetail.totalEstimation = newData.auDetail.totalClient;
    });

    // Livraison
    newData.livraison.totalClient = order?.deliveryCost || 0;
    newData.livraison.totalEstimation = newData.livraison.totalClient;

    // TotalHT
    newData.totalHT.totalClient = [
      "forfaitPiecesHT",
      "forfaitSoftsHT",
      "forfaitAlcoolsHT",
      "materielService",
      "auDetail",
      "livraison",
    ].reduce((total, key) => total + newData[key].totalClient, 0);
    newData.totalHT.totalEstimation = [
      "forfaitPiecesHT",
      "forfaitSoftsHT",
      "forfaitAlcoolsHT",
      "materielService",
      "auDetail",
      "livraison",
    ].reduce((total, key) => total + newData[key].totalEstimation, 0);

    return newData;
  };

  useEffect(() => {
    const newData = initializeData(order.priceInfo, isClassicMode);
    setData(newData);
    // En mode classique, on ne met pas à jour les prix par participant
    if (!isClassicMode) {
      dispatch(
        updatePriceInfo(orderIndex, {
          ...order.priceInfo,
          catererPackagePiecesPriceHtPerParticipant:
            newData.forfaitPiecesHT.parPersonne,
          catererPackageSoftsPriceHtPerParticipant: newData.forfaitSoftsHT.parPersonne,
          catererPackageAlcoholsPriceHtPerParticipant:
            newData.forfaitAlcoolsHT.parPersonne,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderIndex, items, numberOfPersons, useClassicPricing]);

  const handleCellChange = (row, column, value) => {
    const currentIsClassicMode = order.priceInfo?.useClassicPricing === true;
    let newData = initializeData(order.priceInfo, currentIsClassicMode, row, column, value);
    setData(newData);
    // En mode classique, on ne met pas à jour les prix par participant
    if (!currentIsClassicMode) {
      dispatch(
        updatePriceInfo(orderIndex, {
          ...order.priceInfo,
          catererPackagePiecesPriceHtPerParticipant: newData.forfaitPiecesHT.parPersonne,
          catererPackageSoftsPriceHtPerParticipant: newData.forfaitSoftsHT.parPersonne,
          catererPackageAlcoholsPriceHtPerParticipant: newData.forfaitAlcoolsHT.parPersonne,
        })
      );
    }
  };

  const handleClassicPricingChange = (event) => {
    const newValue = event.target.checked;
    dispatch(
      updatePriceInfo(orderIndex, {
        ...order.priceInfo,
        useClassicPricing: newValue,
      })
    );
  };

  const renderRow = (rowKey, rowData, index) => {
    // En mode classique, on cache seulement le sous-total des forfaits (car les totaux sont calculés différemment)
    if (isClassicMode && rowKey === 'forfaitSubTotal') {
      return null;
    }
    return (
      <tr key={rowKey}>
        <td>{rowData.label}</td>
        {Object.keys(rowData).map(
          (colKey) =>
            colKey !== "label" && (
              <EditableCell
                key={colKey}
                value={rowData[colKey]}
                onChange={(value) => handleCellChange(rowKey, colKey, value)}
                editable={colKey === "parPersonne" && index < 3 && !isClassicMode}
              />
            )
        )}
      </tr>
    );
  };

  // En mode brique, on n'affiche ni la case "Utiliser le mode classique
  // de tarification" ni son tableau de forfaits. La tarification est
  // gérée par BriquesSection dans ce mode.
  if (briqueMode) {
    return null;
  }

  return (
    <div>
      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          id="useClassicPricing"
          label="Utiliser le mode classique de tarification"
          checked={isClassicMode}
          onChange={handleClassicPricingChange}
        />
        <Form.Text className="text-muted">
          En mode classique, les prix sont calculés directement à partir des articles.
          En mode forfait, les prix sont calculés par participant pour les forfaits.
        </Form.Text>
      </Form.Group>
      {!isClassicMode && (
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Tarif</th>
              <th>Par personne</th>
              <th>Par personne estimation</th>
              <th>Total client</th>
              <th>Total estimation</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(data).map((rowKey, index) =>
              renderRow(rowKey, data[rowKey], index)
            )}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default PricingTable;
